import axios from "axios";
import { API_BASE_URL } from "../../../../constants";
import { refreshEntitlement } from "../../../../store/actions/entitlementRefresh";
import { removeVerifiedTeam } from "../../../auth/Onboarding/services/onboardingServices";

// Saves the whole athlete document the way the classic editor does: the
// backend resolves institution names to codes, keeps history lists and
// re-checks profile completion.
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

// Field messages from a refused save ({error: 'invalid', fields: {...}}), one
// string per field.
const fieldMessages = (fields) => {
  const out = {};
  if (!fields || typeof fields !== "object" || Array.isArray(fields)) return out;
  Object.entries(fields).forEach(([key, value]) => {
    const text = Array.isArray(value) ? value[0] : value;
    if (typeof text === "string" && text.trim()) out[key] = text.trim();
  });
  return out;
};

// A refused save throws an Error whose message is the server's words (its
// `message`, else an `error` or `detail` sentence, never a bare code such as
// 'invalid', else the first field message) and whose `fields` carries each
// field's message for the form.
export const saveProfileDoc = async (payload) => {
  try {
    const res = await axios.put(`${API_BASE_URL}/athlete/update/`, payload, { headers: headers() });
    return res.data;
  } catch (e) {
    const d = e.response?.data;
    const body = d && typeof d === "object" ? d : {};
    const text = (v) => (typeof v === "string" ? v.trim() : "");
    const fields = fieldMessages(body.fields);
    const sentence = [text(body.error), text(body.detail)].find((v) => v && !/^[a-z0-9_.-]+$/.test(v));
    const plain = typeof d === "string" && !/^\s*</.test(d) ? d.trim() : "";
    const err = new Error(text(body.message) || sentence || Object.values(fields)[0] || plain || "Could not save your profile.");
    err.fields = fields;
    throw err;
  }
};

// The athlete's teams: the main team (the one the score is built on) first,
// then every other team. A refused change throws an Error carrying the
// server's sentence, with the body on err.data ({error, message}) and the
// HTTP status on err.status. A request the server could not answer (a 5xx)
// never shows its body's text, which can carry a data vendor's name or a
// raw error (10/19 notes, D16): only CACHE's own sentences for team data
// that is down (code provider_unavailable) or a program's team that could
// not be made just now (team_unavailable).
const NEUTRAL_5XX = new Set(["team_unavailable"]);
const teamsError = (e) => {
  const d = e?.response?.data;
  const data = d && typeof d === "object" ? d : {};
  const status = e?.response?.status;
  const text = (v) => (typeof v === "string" ? v.trim() : "");
  let message;
  if (status >= 500) {
    message = data.code === "provider_unavailable" ? text(data.error) || text(data.message)
      : NEUTRAL_5XX.has(data.error) ? text(data.message) : "";
    message = message || "We could not update your teams. Try again in a minute.";
  } else {
    const sentence = [text(data.error), text(data.detail)].find((v) => v && !/^[a-z0-9_.-]+$/.test(v));
    message = text(data.message) || sentence || "We could not update your teams.";
  }
  const err = new Error(message);
  err.status = status;
  err.data = data;
  return err;
};
const teamsCall = async (method, path, body) => {
  try {
    const res = await axios({ method, url: `${API_BASE_URL}/${path}`, data: body, headers: headers() });
    return res.data || {};
  } catch (e) {
    throw teamsError(e);
  }
};
// A team added, removed or made the main team can start or end the Premium
// a coach's paid plan includes, so the entitlement is fetched again after
// the change (in the background; it never throws).
const changeTeams = async (method, path, body) => {
  const out = await teamsCall(method, path, body);
  refreshEntitlement();
  return out;
};

// -> {teams}: each item says whether it can be made the main team
// (canMakePrimary), whether that replaces the main team because both are
// the same team from two listings (replacesPrimary), how people read it
// (displayName, with a squad's age group) and what tells two listings of
// one team apart (listingLabel: a league or level words, never a data
// vendor's name).
export const fetchMyTeams = () => teamsCall("get", "athletes/teams/");
// {league, teamId, teamSource, teamName, leagueLabel?, playerId?,
// programOnly?, pendingRosterPick?} -> {teams}. With no main team yet the
// team becomes the main team.
export const addMyTeam = (payload) => changeTeams("post", "athletes/teams/add/", payload);
// The Club or Team field picks a real team (notes of 10/19, K4):
// {slot: 'club' | 'team' (or '' to claim a spot on a listed team), teamId,
// league, teamSource, teamName, leagueLabel?, playerId?, programOnly?,
// pendingRosterPick?}. The team becomes a binding (the main team when there
// is none, else another team) and the field is written from it.
// -> {teams, slot, teamId, league, bound, fillNote?}.
export const setMyTeamSlot = (payload) => changeTeams("post", "athletes/teams/slot/", payload);
// Only a team that is not the main one. -> {teams, status}; status goes back
// to unverified when the last verified team goes.
export const removeMyTeam = ({ teamId, league }) => changeTeams("post", "athletes/teams/remove/", { teamId, league });
// Makes another team on the list the main team, the one the stats and the
// CacheScore come from. The old main team stays on the list as another team,
// or goes when it is the same team from another source (`replaced`).
// -> {teams, primary, replaced, message}. Refusals: 404 not_on_profile,
// 409 already_primary or profile_taken (another athlete holds the roster spot).
export const makeMyTeamPrimary = ({ teamId, league }) => changeTeams("post", "athletes/teams/primary/", { teamId, league });
// A team listed with no id (a coach code from a coach whose team has only a
// name): athletes/teams/remove/ needs an id, so the coach-code remove finds it
// by name (that request fetches the entitlement again itself).
// -> {teams: the verified teams only, status, message}
export const removeMyTeamByName = async ({ teamName }) => {
  try {
    return (await removeVerifiedTeam({ teamName })) || {};
  } catch (e) {
    throw teamsError(e);
  }
};

export const deleteAvatar = async (email) => {
  await axios.delete(`${API_BASE_URL}/athlete/avatar/delete/${encodeURIComponent(email)}/`, { headers: headers() });
};

// searchTeams: the team search the Club and Team fields offer (every league
// and CACHE's own teams; rows carry dataStatus while the team feed is down).
export { uploadAvatar, compressImage, getOnboarding, recomputeSelf, waitFor, searchTeams } from "../onboarding/onboardingV2Services";
